<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('api.tag.tags') }}

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'apis.tags.create' }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('addNew') }}</span>
                        <span class="hidden-xs">{{ $t('addNew') }}</span>
                        <span class="visible-xs"><i class="fa fa-plus"></i></span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body"
                        >
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else-if="tags.length">
                            <div class="box-header with-border">
                                <h3 class="box-title">
                                    {{ $t('api.tag.tagsTable') }}
                                </h3>
                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapse = !isCollapse"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapse,
                                                'fa-minus': isCollapse,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapse">
                                <div
                                    v-if="paginationOptions.total"
                                    class="box-body with-border clearfix"
                                >
                                    <BadgeTotal
                                        :value="paginationOptions.total"
                                        color="gray"
                                        :label="`${$t('total')}: `"
                                    />

                                    <div
                                        v-if="paginationOptions.last_page > 1"
                                        class="pagination-box"
                                    >
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchTags"
                                        />
                                    </div>
                                </div>

                                <div class="box-body table-responsive no-padding">
                                    <table class="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('id') }}</th>

                                                <th>
                                                    {{ $t('name') }}
                                                </th>

                                                <th>
                                                    {{ $t('api.apis') }}
                                                </th>

                                                <th class="text-right">
                                                    {{ $t('createdAt') }}
                                                </th>

                                                <th class="text-right">
                                                    {{ $t('updatedAt') }}
                                                </th>

                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                                v-for="tag in tags"
                                                :key="tag.id"
                                            >
                                                <td>{{ tag.id }}</td>

                                                <td>{{ tag.name }}</td>

                                                <td>
                                                    <BadgeTotal
                                                        :value="tag.apisCount"
                                                        color="gray"
                                                    />
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(tag.createdAt) }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(tag.updatedAt) }}
                                                </td>

                                                <td class="text-right text-nowrap">
                                                    <router-link
                                                        :to="{
                                                            name: 'apis.tags.show',
                                                            params: {
                                                                id: tag.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-info"
                                                        :title="$t('show')"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-eye" />
                                                        <span class="sr-only">
                                                            {{ $t('show') }}
                                                        </span>
                                                    </router-link>

                                                    <router-link
                                                        :to="{
                                                            name: 'apis.tags.edit',
                                                            params: {
                                                                id: tag.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-primary"
                                                        :title="$t('edit')"
                                                        data-toggle="tooltip"
                                                    >
                                                        <i class="fa fa-edit" />
                                                        <span class="sr-only">
                                                            {{ $t('edit') }}
                                                        </span>
                                                    </router-link>

                                                    <button
                                                        type="button"
                                                        class="btn btn-sm btn-danger"
                                                        :title="$t('delete')"
                                                        data-toggle="modal"
                                                        data-target="#delete-modal"
                                                        @click="tagDeleting = tag"
                                                    >
                                                        <i class="fa fa-trash" />
                                                        <span class="sr-only">
                                                            {{ $t('delete') }}
                                                        </span>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    v-if="paginationOptions.last_page > 1"
                                    class="box-footer clearfix"
                                >
                                    <div class="pagination-box">
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchTags"
                                        />
                                    </div>
                                </div>
                            </vue-slide-up-down>
                        </template>

                        <div
                            v-else
                            class="box-body with-border clearfix"
                        >
                            <p>
                                {{ $t('messages.api.tag.noTags') }}
                                <router-link :to="{ name: 'apis.tags.create' }">
                                    {{ $t('addNew') }}
                                </router-link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ApisTagsModalDelete
            :tag="tagDeleting"
            @deleted="onDeleted"
            @closed="tagDeleting = {}"
        />
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ApisTagsModalDelete from '@/components/apis/tags/ApisTagsModalDelete';
import formatsMixin from '@/mixins/formatsMixin';

export default {
    components: {
        PaginationTable,
        BadgeTotal,
        ApisTagsModalDelete,
    },
    mixins: [formatsMixin],
    data() {
        return {
            isCollapse: true,
            isLoaded: false,
            tagDeleting: {},
            tags: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
        };
    },
    created() {
        this.fetchTags(this.$route.query.page);
    },
    methods: {
        async fetchTags(page = 1) {
            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        tags: { data: tags, ...paginationOptions },
                    },
                } = await axios.get('/apis/tags/data?page=' + page);

                this.tags = tags;
                this.paginationOptions = paginationOptions;
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
                this.paginationOptions.disabled = false;
            }
        },
        async onDeleted() {
            this.tagDeleting = {};
            this.fetchTags();
        },
    },
};
</script>
